<script>
import utilsMixin from '@shared/mixins/utils';

const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'cancel_at_period_end', 'past_due'];

export default {
  mixins: [utilsMixin],
  props: {
    isEmbed: {
      type: Boolean,
      default: false,
    },
    hasPrice: {
      type: Boolean,
      default: true,
    },
    membership: {
      type: Object,
      required: true,
    },
  },
  i18n: {
    messages: {
      fr: {
        includes_content: 'inclut {count} contenu',
        includes_contents: 'inclut {count} contenus',
        starting_at: 'À partir de',
        view_membership: 'Voir l\'abonnement',
        already_subscribed: 'Vous êtes déjà abonné à cet abonnement',
        no_price_available: 'Prix non disponible',
      },
      en: {
        includes_content: 'includes {count} content',
        includes_contents: 'includes {count} contents',
        starting_at: 'Starting at',
        view_membership: 'View Membership',
        already_subscribed: 'You are already subscribed to this membership',
        no_price_available: 'Price not available',
      },
    },
  },
  computed: {
    activePricesCount() {
      if (!Array.isArray(this.membership.prices)) {
        return 0;
      }

      return this.membership.prices.filter((price) => price.status === 'active').length
        || this.membership.prices.length;
    },
    href() {
      return this.$router
        .resolve({ name: 'subscription', params: { slug: this.membership.slug } })
        .href;
    },
    picture() {
      if (this.membership.picture && !this.membership.picture.startsWith('http')) {
        return `${this.$env.apiResourcesURL}/${this.membership.picture}`;
      }
      if (!this.membership.picture) {
        this.$emit('img-load');
      }
      return this.membership.picture;
    },
    lowestPrice() {
      if (!this.membership.prices || this.membership.prices.length === 0) {
        return null;
      }

      const sortedPrices = [...this.membership.prices].sort((a, b) => {
        const aPrice = a.discount_price && a.discount_price > 0 ? a.discount_price : a.amount;
        const bPrice = b.discount_price && b.discount_price > 0 ? b.discount_price : b.amount;
        return aPrice - bPrice;
      });
      return sortedPrices[0];
    },
    priceDisplay() {
      if (!this.lowestPrice) {
        return this.$t('no_price_available');
      }

      const effectivePrice = this.lowestPrice.discount_price && this.lowestPrice.discount_price > 0
        ? this.lowestPrice.discount_price
        : this.lowestPrice.amount;

      const lang = this.$i18n?.locale || 'fr';
      const intervalText = this.formatIntervalPer(this.lowestPrice, lang);

      if (this.activePricesCount <= 1) {
        return `${this.formatPrice(effectivePrice)} ${intervalText}`.trim();
      }

      return `${this.$t('starting_at')} ${this.formatPrice(effectivePrice)} ${intervalText}`.trim();
    },
    customerMemberships() {
      return this.$store?.getters?.['auth/customerMemberships'] || [];
    },
    isAlreadySubscribed() {
      if (!this.membership) {
        return false;
      }

      const membershipUuid = this.membership.uuid;
      const membershipId = this.membership.id;

      return this.customerMemberships.some((subscription) => {
        if (!subscription || !ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status)) {
          return false;
        }

        const subscriptionMembership = subscription.membership || {};

        if (membershipUuid && subscriptionMembership?.uuid === membershipUuid) {
          return true;
        }

        if (membershipId && subscription.membership_id) {
          return String(subscription.membership_id) === String(membershipId);
        }

        return false;
      });
    },
  },
  methods: {
    formatPrice(price) {
      return this.$options.filters.formatPrice(price);
    },
    onClick() {
      if (this.isEmbed) {
        window.parent.postMessage({ name: 'checkout' }, '*');
        return;
      }
      this.$router.push({ name: 'subscription', params: { slug: this.membership.slug } });
    },
  },
};
</script>

<template>
  <article class="comp box">
    <div class="columns is-vcentered">
      <div class="column is-narrow">
        <figure class="comp_img image bradius-8 cursor-pointer" @click.prevent="onClick">
          <img v-if="picture" :src="picture" :alt="membership.name" @load="$emit('img-load')">
          <span v-else class="is-block p-10 has-background-light is-custom has-text-centered">
            <b-icon icon="credit-card" size="is-large" />
          </span>
          <div class="comp_img_info">
            <b-tag v-if="membership.items && membership.items.length <= 1" type="is-primary">
              {{ $t('includes_content', { count: membership.items ? membership.items.length : 0 }) }}
            </b-tag>
            <b-tag v-else-if="membership.items && membership.items.length > 1" type="is-primary">
              {{ $t('includes_contents', { count: membership.items.length }) }}
            </b-tag>
          </div>
        </figure>
      </div>
      <div class="column">
        <h2 class="title is-custom is-4 is-size-6-touch">
          <a class="color-inherit" :href="href" @click.prevent="onClick">
            {{ membership.name }}
          </a>
        </h2>
        <p v-if="membership.summary" class="is-size-6 is-size-8-touch">
          {{ membership.summary }}
        </p>
        <p v-if="!isAlreadySubscribed" class="mt-4 is-size-6 is-size-8-touch">
          <template v-if="hasPrice">
            <strong class="mr-3 valign-middle">
              {{ priceDisplay }}
            </strong>
          </template>
          <b-button
            type="is-primary is-custom valign-middle"
            tag="a"
            :href="href"
            @click.prevent="onClick"
          >
            {{ $t('view_membership') }}
          </b-button>
        </p>
        <p
          v-else
          class="tag"
        >
          {{ $t('already_subscribed') }}
        </p>
      </div>
    </div>
  </article>
</template>

<style lang="scss" scoped>
.comp {
  &_img {
    width: 320px;
    max-height: 320px;
    position: relative;
    img {
      object-fit: cover;
      object-position: center top;
    }
    .is-small & {
      width: 200px;
    }
    &_info {
      position: absolute;
      bottom: 8px;
      right: 8px;
    }
  }
}

.cursor-pointer {
  cursor: pointer;
}

.color-inherit {
  color: inherit;
  text-decoration: none;
}

.valign-middle {
  vertical-align: middle;
}
</style>
